import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../api";
import { PortalLayout } from "../components/Portal";
import { G_DARK } from "../components/Shared";

/**
 * The one thing `?path=` may be: a path on this site under `/api/`.
 *
 * It is handed to `fetchFile`, which attaches the session token, so anything
 * else would make this page send the token wherever a link pointed — or serve
 * as an open redirect. Mirrors LoginPage's `safeNext`: no scheme, no `//` or
 * `\`, no control characters; and the path is resolved against this origin, so
 * `/api/../login` is caught by what it means rather than by how it is spelt.
 * Returns the path unchanged, or null.
 */
export function safeApiPath(path) {
  if (typeof path !== "string" || !/^\/api\//.test(path)) return null;
  // eslint-disable-next-line no-control-regex
  if (/[\\\u0000-\u001f\u007f]/.test(path)) return null;
  let resolved;
  try { resolved = new URL(path, window.location.origin); } catch { return null; }
  if (resolved.origin !== window.location.origin) return null;
  if (!resolved.pathname.startsWith("/api/")) return null;
  return path;
}

const BUTTON = {
  display: "inline-flex", alignItems: "center", justifyContent: "center", minHeight: 44,
  padding: "9px 20px", borderRadius: 8, fontSize: 14, fontWeight: 600,
  textDecoration: "none", fontFamily: "inherit", boxSizing: "border-box",
};

/**
 * A PDF in a portal page — where `api.openFile` sends every "open" (2026-10-06).
 *
 * The tab used to be a bare `blob:` URL: no header, no name, no way back, and a
 * reload that failed once the blob was revoked a minute later. Here the blob
 * lives exactly as long as the page, and a reload fetches the file again.
 *
 * Download is always shown, above the frame: mobile Safari often renders a PDF
 * in an iframe as its first page or not at all, and on a phone the button is
 * the reliable way to the document.
 */
export default function PdfViewPage() {
  const [params] = useSearchParams();
  const path = safeApiPath(params.get("path"));
  const titleHint = params.get("title") || "";
  const [file, setFile] = useState(null);   // { url, filename }
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!path) return undefined;
    let alive = true;
    let url = null;
    setFile(null);
    setError(null);
    (async () => {
      try {
        const { blob, filename } = await api.fetchFile(path);
        url = URL.createObjectURL(blob);
        if (!alive) { URL.revokeObjectURL(url); return; }
        setFile({ url, filename });
      } catch (err) {
        // An expired session is already being handled by api.js — it signs
        // the user out and this page is on its way to /login.
        if (alive && err.name !== "AuthExpiredError") {
          setError(err.message || "Could not fetch the document");
        }
      }
    })();
    return () => {
      alive = false;
      if (url) URL.revokeObjectURL(url);
    };
  }, [path]);

  const name = file?.filename || titleHint || "Document";

  useEffect(() => {
    const before = document.title;
    document.title = name;
    return () => { document.title = before; };
  }, [name]);

  // Opened from the portal: closing the tab returns to the page that opened it,
  // where the user was. Opened any other way (a bookmark, a reload of a tab
  // whose opener is gone): the link itself goes to the portal. Not prevented
  // either way, so a close the browser refuses still leaves a way back.
  const back = () => { if (window.opener) window.close(); };

  return (
    <PortalLayout>
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <Link to="/" onClick={back} style={{ color: G_DARK, fontSize: 14, fontWeight: 600,
                                              alignSelf: "flex-start", padding: "10px 0" }}>
          ← Back to portal
        </Link>
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center",
                      justifyContent: "space-between", gap: 12 }}>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, overflowWrap: "anywhere",
                       minWidth: 0 }}>
            {path ? name : "Document"}
          </h1>
          {file && (
            <a href={file.url} download={file.filename}
               style={{ ...BUTTON, background: G_DARK, color: "#fff" }}>
              Download
            </a>
          )}
        </div>
        {!path && (
          <div role="alert" style={{ color: "#b91c1c", fontSize: 14 }}>
            This link is not a portal document, so nothing was opened.
          </div>
        )}
        {path && error && (
          <div role="alert" style={{ color: "#b91c1c", fontSize: 14 }}>{error}</div>
        )}
        {path && !file && !error && (
          <div role="status" style={{ color: "#6b7280", fontSize: 14 }}>Loading the document…</div>
        )}
        {file && (
          <iframe title={name} src={file.url}
                  style={{ width: "100%", height: "80vh", border: "1px solid #e5e7eb",
                           borderRadius: 8, background: "#fff" }} />
        )}
      </div>
    </PortalLayout>
  );
}
